
import React from 'react';

interface SimpleMarkdownProps {
  content: string;
}

// A very simple markdown renderer that handles basic formatting
export const SimpleMarkdown: React.FC<SimpleMarkdownProps> = ({ content }) => {
  if (!content) return null;
  
  // Pre-process content to handle lists
  let processedContent = content;
  
  // Process bullet lists
  let inBulletList = false;
  let bulletListContent = '';
  
  processedContent = processedContent.split('\n').map(line => {
    if (line.trim().startsWith('- ')) {
      const itemContent = line.trim().substring(2);
      
      if (!inBulletList) {
        inBulletList = true;
        bulletListContent = `<ul class="list-disc pl-6 my-3 text-left"><li class="text-left">${itemContent}</li>`;
        return '';
      } else {
        bulletListContent += `<li class="text-left">${itemContent}</li>`;
        return '';
      }
    } else if (inBulletList) {
      inBulletList = false;
      const result = `${bulletListContent}</ul>\n${line}`;
      bulletListContent = '';
      return result;
    }
    
    return line;
  }).join('\n');
  
  // Close any open bullet list at the end
  if (inBulletList) {
    processedContent += `${bulletListContent}</ul>`;
  }
  
  // Process numbered lists
  let inNumberedList = false;
  let numberedListContent = '';
  
  processedContent = processedContent.split('\n').map(line => {
    if (/^\d+\.\s/.test(line.trim())) {
      const itemContent = line.trim().replace(/^\d+\.\s+/, '');
      
      if (!inNumberedList) {
        inNumberedList = true;
        numberedListContent = `<ol class="list-decimal pl-6 my-3 text-left"><li class="text-left">${itemContent}</li>`;
        return '';
      } else {
        numberedListContent += `<li class="text-left">${itemContent}</li>`;
        return '';
      }
    } else if (inNumberedList) {
      inNumberedList = false;
      const result = `${numberedListContent}</ol>\n${line}`;
      numberedListContent = '';
      return result;
    }
    
    return line;
  }).join('\n');
  
  // Close any open numbered list at the end
  if (inNumberedList) {
    processedContent += `${numberedListContent}</ol>`;
  }
  
  // Process the content with basic markdown support
  processedContent = processedContent
    // Handle headers
    .replace(/^### (.*?)$/gm, '<h3 class="text-lg font-bold mt-4 mb-2 text-left">$1</h3>')
    .replace(/^## (.*?)$/gm, '<h2 class="text-xl font-bold mt-4 mb-2 text-left">$1</h2>')
    .replace(/^# (.*?)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-3 text-left">$1</h1>')
    
    // Handle bold and italic
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Handle paragraphs
    .replace(/^\s*$/gm, '</p><p class="my-2 text-left">')
    
    // Handle links
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank">$1</a>')
    
    // Handle images (basic)
    .replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="max-w-full my-2 rounded" />')
    
    // Handle code blocks
    .replace(/```([\s\S]*?)```/g, '<pre class="bg-muted p-2 rounded my-2 overflow-auto text-left"><code>$1</code></pre>')
    
    // Handle inline code
    .replace(/`(.*?)`/g, '<code class="bg-muted px-1 rounded">$1</code>')
    
    // Handle horizontal rule
    .replace(/^---$/gm, '<hr class="my-4 border-t border-border" />');
  
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none text-left" 
         dangerouslySetInnerHTML={{ __html: `<p class="text-left">${processedContent}</p>` }} 
    />
  );
};
